import { z } from 'zod';
import {
  MoneyMetricInputSchema,
  CountMetricInputSchema,
  RatioMetricInputSchema,
  GOAL_PERIODS,
  type MetricRole,
} from '@/domain/metrics/contracts';
import type { PropagationEdgeInput, PropagationNodeInput } from '@/domain/metrics/propagation/propagate';

/**
 * SPEC-009 §5-13/plan §3-5 — scenarios are a pure override layer on top of
 * the SPEC-008 metrics engine (SIM-PRINCIPLE-003). This module stays
 * framework-free and EditorGraph-free: callers hand in plain graph data
 * (the same shape the metrics engine already accepts), never a React/
 * Zustand type.
 */

export const SCENARIO_TYPES = ['conservative', 'probable', 'optimistic', 'custom'] as const;
export type ScenarioType = (typeof SCENARIO_TYPES)[number];

/** SPEC-009 §7/§48 — canonical default scenario type → stable label. Type
 * never changes; the label can be localized independently (§50). */
export const DEFAULT_SCENARIO_LABELS: Record<Exclude<ScenarioType, 'custom'>, string> = {
  conservative: 'Conservative',
  probable: 'Probable',
  optimistic: 'Optimistic',
};

/** SPEC-009 §11 — per-node metric overrides, validated with the exact same
 * schemas SPEC-008 uses for base inputs (plan §10: no duplicated validation). */
export const NodeScenarioOverrideSchema = z
  .object({
    cost: MoneyMetricInputSchema.optional(),
    entries: CountMetricInputSchema.optional(),
    exits: CountMetricInputSchema.optional(),
    conversion: RatioMetricInputSchema.optional(),
    revenue: MoneyMetricInputSchema.optional(),
    averageTicket: MoneyMetricInputSchema.optional(),
  })
  .partial();
export type NodeScenarioOverride = z.infer<typeof NodeScenarioOverrideSchema>;

/** SPEC-009 §11/§32/plan §23 — edge conversion override plus the
 * planning-only `reverseShare` (never a base edge/operational field). */
export const EdgeScenarioOverrideSchema = z
  .object({
    conversion: RatioMetricInputSchema.optional(),
    reverseShare: z.number().finite().min(0).max(1).optional(),
  })
  .partial();
export type EdgeScenarioOverride = z.infer<typeof EdgeScenarioOverrideSchema>;

export const ScenarioOverridesSchema = z.object({
  nodeOverrides: z.record(z.string(), NodeScenarioOverrideSchema).default({}),
  edgeOverrides: z.record(z.string(), EdgeScenarioOverrideSchema).default({}),
});
export type ScenarioOverrides = z.infer<typeof ScenarioOverridesSchema>;

export function emptyScenarioOverrides(): ScenarioOverrides {
  return { nodeOverrides: {}, edgeOverrides: {} };
}

// --- Simulation target / reverse -------------------------------------------

export const SIMULATION_TARGET_METRICS = ['clients', 'revenue', 'entries', 'exits'] as const;
export type SimulationTargetMetric = (typeof SIMULATION_TARGET_METRICS)[number];

export const SimulationTargetSchema = z.object({
  metric: z.enum(SIMULATION_TARGET_METRICS),
  value: z.number().finite().nonnegative(),
  nodeId: z.string().optional(),
  period: z.enum(GOAL_PERIODS).optional(),
  currency: z.string().optional(),
});
export type SimulationTarget = z.infer<typeof SimulationTargetSchema>;

// --- Scenario ----------------------------------------------------------------

export const ScenarioSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(80),
  type: z.enum(SCENARIO_TYPES),
  description: z.string().max(500).optional(),
  overrides: ScenarioOverridesSchema,
  target: SimulationTargetSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  isDefault: z.boolean().optional(),
});
export type Scenario = z.infer<typeof ScenarioSchema>;

/** SPEC-009 §16/plan §6 — the only thing registered on `GraphDocument.simulation`. */
export const SimulationGraphExtensionSchema = z.object({
  scenarios: z.array(ScenarioSchema).default([]),
  activeScenarioId: z.string().nullable().default(null),
});
export type SimulationGraphExtension = z.infer<typeof SimulationGraphExtensionSchema>;

export function emptySimulationExtension(): SimulationGraphExtension {
  return { scenarios: [], activeScenarioId: null };
}

// --- Plain graph input (same shape the metrics engine consumes) ------------

export interface SimulationNodeInput extends PropagationNodeInput {
  roles: readonly MetricRole[];
  includeInFunnelInvestment?: boolean;
}
export interface SimulationGraphInput {
  nodes: readonly SimulationNodeInput[];
  edges: readonly PropagationEdgeInput[];
}

// --- Value origin (scenario-specific, distinct from MetricOrigin) ----------

export const SCENARIO_VALUE_ORIGINS = ['base', 'scenario_override', 'calculated', 'reverse_required'] as const;
export type ScenarioValueOrigin = (typeof SCENARIO_VALUE_ORIGINS)[number];

// --- Target status -------------------------------------------------------

export const TARGET_STATUSES = ['met', 'below', 'above', 'unknown', 'unreachable', 'ambiguous'] as const;
export type TargetStatus = (typeof TARGET_STATUSES)[number];

// --- Reverse result --------------------------------------------------------

export interface ReverseRequirement {
  nodeId: string;
  metric: 'entries' | 'exits';
  rawRequired: number | null;
  required: number | null;
  current: number | null;
  gap: number | null;
  status: TargetStatus;
  rounding?: 'ceiling' | 'none';
}

export interface ReverseEdgeRequirement {
  edgeId: string;
  requiredFlow: number | null;
  currentFlow: number | null;
  status: TargetStatus;
}

export interface ReverseResult {
  target: SimulationTarget;
  requiredNodeMetrics: Map<string, ReverseRequirement[]>;
  requiredEdgeMetrics: Map<string, ReverseEdgeRequirement>;
  /** Node/edge IDs where traversal stopped without a resolved requirement. */
  unresolved: string[];
  warnings: import('./warnings').SimulationWarning[];
  explanation: {
    targetNodeId: string | null;
    targetMetric: 'entries' | 'exits' | null;
    derivedFromRevenue?: { requiredClients: number; averageTicket: number };
  };
}

/** Helper to build stable scenario/edge/node identifiers. */
export function createScenarioId(): string {
  return crypto.randomUUID();
}

export function nowIso(): string {
  return new Date().toISOString();
}
